import React, { useMemo } from 'react';

import { IGridExtendedFilters } from 'utils/grid/types';

import { IFilterContext, IFilterContextProviderProps } from './types';
import { useFiltration } from './useFiltration';

const FilterContext = React.createContext<IFilterContext>({
  config: {
    failureCount: 0,
    filtersSections: new Map() as IGridExtendedFilters,
    filtersSectionsMap: {},
    hidePresets: false,
    requestFiltersPrefix: '',
  },
  filters: {},
  gridColumnsSettings: null,
  isRedirectedPreset: false,
  onPageFiltersChange: () => {},
  setIsRedirectedPreset: () => {},

  canFilter: false,
  clearAllFilters: () => {},
  clearFocusedItem: () => {},
  clearSectionFilters: () => {},
  conflictColumns: [],
  focusedFieldName: null,
  isCreatingShortLink: false,
  isSidebarOpen: false,
  onApply: () => {},
  onCloseSidebar: () => {},
  onOpenSidebar: () => {},
  onSelectFilter: () => {},
  onSelectOperation: () => {},
  onShareFilters: () => {},
  onToggleSidebar: () => {},
  removeFilter: () => ({}),
  rollBackBrokenFilters: () => {},
  setCanFilter: () => {},
  setFocusedFieldName: () => {},
  setIsSidebarOpen: () => {},
  setSidebarFilters: () => {},
  sidebarFilters: {},
  toggleAdditionalCondition: () => {},
  toggleFilterEnabled: () => ({}),
});

export const FilterContextProvider: React.FC<IFilterContextProviderProps> = ({
  children,
  filtersConfig,
  gridColumnsSettings,
  isRedirectedPreset,
  lastNavigationAction,
  onPageFiltersChange,
  pageFilters,
  pageKey,
  rollBackBrokenFilters,
  searchString,
  setIsRedirectedPreset,
}) => {
  const {
    canFilter,
    conflictColumns,
    focusedFieldName,
    isCreatingShortLink,
    isSidebarOpen,
    setCanFilter,
    setFocusedFieldName,
    setIsSidebarOpen,
    setSidebarFilters,
    sidebarFilters,

    clearAllFilters,
    clearFocusedItem,
    clearSectionFilters,
    onApply,
    onCloseSidebar,
    onOpenSidebar,
    onSelectFilter,
    onSelectOperation,
    onShareFilters,
    onToggleSidebar,
    removeFilter,
    toggleAdditionalCondition,
    toggleFilterEnabled,
  } = useFiltration({
    filtersConfig,
    gridColumnsSettings,
    onPageFiltersChange,
    pageFilters,
    pageKey,
  });

  const sidebarContext = useMemo<IFilterContext>(() => {
    return {
      config: filtersConfig || {},
      filters: pageFilters?.search,
      gridColumnsSettings: gridColumnsSettings || null,
      isRedirectedPreset,
      onPageFiltersChange,
      pageKey,
      searchString,
      setIsRedirectedPreset,

      canFilter,
      clearAllFilters,
      clearFocusedItem,
      clearSectionFilters,
      conflictColumns,
      focusedFieldName,
      isCreatingShortLink,
      isSidebarOpen,
      lastNavigationAction,
      onApply,
      onCloseSidebar,
      onOpenSidebar,
      onSelectFilter,
      onSelectOperation,
      onShareFilters,
      onToggleSidebar,
      removeFilter,
      rollBackBrokenFilters,
      setCanFilter,
      setFocusedFieldName,
      setIsSidebarOpen,
      setSidebarFilters,
      sidebarFilters,
      toggleAdditionalCondition,
      toggleFilterEnabled,
    };
  }, [
    rollBackBrokenFilters,
    filtersConfig,
    pageFilters?.search,
    gridColumnsSettings,
    isRedirectedPreset,
    onPageFiltersChange,
    pageKey,
    searchString,
    setIsRedirectedPreset,
    lastNavigationAction,
    canFilter,
    clearAllFilters,
    clearFocusedItem,
    clearSectionFilters,
    conflictColumns,
    focusedFieldName,
    isCreatingShortLink,
    isSidebarOpen,
    onApply,
    onCloseSidebar,
    onOpenSidebar,
    onSelectFilter,
    onSelectOperation,
    onShareFilters,
    onToggleSidebar,
    removeFilter,
    setCanFilter,
    setFocusedFieldName,
    setIsSidebarOpen,
    setSidebarFilters,
    sidebarFilters,
    toggleAdditionalCondition,
    toggleFilterEnabled,
  ]);

  return (
    <FilterContext.Provider value={sidebarContext}>{children}</FilterContext.Provider>
  );
};

export const useFilterContext = () => React.useContext(FilterContext);
